import React, { Component } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import GIF from 'gif.js';
import { Canvg } from 'canvg'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

/**
 * Represents a component for rendering and animating an SVG diagram.
 *
 * @component
 * 
 * @param {object} props - The properties passed to the component.
 * 
 * @function componentDidMount - React lifecycle method that is called after a component is mounted. No input or output.
 * 
 * @function componentDidUpdate - React lifecycle method that is called after a component is updated. 
 * @param {object} prevProps - The previous props. No output.
 * 
 * @function handleExport - Handles the event of exporting the SVG as a GIF. 
 * @param {object} event - The event object. Returns: Nothing.
 * 
 * @function generateGif - Generates a GIF from the SVG drawings. 
 * @param {boolean} download - A boolean indicating whether to download the GIF. Returns: Nothing.
 * 
 * @function handleBpmChange - Handles the event of changing the BPM (Beats Per Minute). 
 * @param {object} event - The event object. Returns: Nothing.
 * 
 * @function getBpmDelay - Calculates the delay based on the BPM. No input. Returns: The delay in milliseconds.
 * 
 * @function calculateNoteDuration - Calculates the duration of a note based on its value. 
 * @param {number} noteValue - The value of the note. Returns: The duration of the note in milliseconds.
 * 
 * @function render - React lifecycle method that renders the component. No input or output.
 */
class SVGAnimationComponent extends Component {
    constructor(props) {
        super(props);
        this.renderRef = React.createRef();
        this.imageRef = React.createRef();
        this.validDrawings = props.drawings;
        this.currentIndex = 0;
        this.state = {
            bpm: 240,
        };
        this.gifEncoder = null;
    }

    componentDidMount() {

    }

    componentDidUpdate(prevProps) {
        if (this.props.drawings !== prevProps.drawings) {
            if (this.props.drawings.length === 0) return;
            this.validDrawings = this.props.drawings;
            this.generateGif();
        }
    }

    handleExport = async (event) => {
        event.preventDefault();
        this.generateGif(true);
    };

    async generateGif(download = false) {
        const renderCanvas = this.renderRef.current;
        const imageElement = this.imageRef.current;
        if (this.validDrawings.length === 0) return;
        // Reset the GIF encoder
        this.gifEncoder = new GIF({
            workers: 2,
            quality: 1,
            width: this.props.width || 750,
            height: this.props.height || 144,
            workerScript: "/modules/contrib/gtc/js/gtc-fretboard/node_modules/gif.js/dist/gif.worker.js",
        });

        // Add each drawing to the GIF encoder
        for (const drawing of this.validDrawings) {
            const ctx = renderCanvas.getContext('2d', { willReadFrequently: true });
            ctx.imageSmoothingEnabled = true;
            ctx.willReadFrequently = true;
            const v = await Canvg.from(ctx, drawing.svg);
            v.start();
            this.gifEncoder.addFrame(ctx, { copy: true, delay: this.calculateNoteDuration(drawing.timing) });
        }

        // Start the GIF encoder and render the GIF to the canvas

        this.gifEncoder.on('finished', (blob) => {
            if (download) {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "guitartabcreator_animated_fretboard.gif";
                a.click();
                URL.revokeObjectURL(url);
            } else {
                const dataUrl = URL.createObjectURL(blob);
                imageElement.src = dataUrl;
            }
        });

        this.gifEncoder.on('progress', (progress) => {
        });

        this.gifEncoder.render();
    }


    handleBpmChange = (event) => {
        this.setState({ bpm: event.target.value }, () => {
            this.generateGif();
        });
    };

    getBpmDelay = () => {
        const bpm = parseInt(this.state.bpm, 10);
        const fps = 30; // frames per second
        const delay = Math.round(1000 / fps); // delay in milliseconds
        return Math.round((60 / bpm) * fps * delay / 1000);
    };

    calculateNoteDuration = (noteValue) => {
        // Check if the note value is valid.
        if (![4, 2, 1, 0.5, 0.25].includes(noteValue)) {
            return "Invalid note value";
        }
        // Calculate the delay for the selected note in milliseconds.
        const quarterNoteDuration = 60 / parseInt(this.state.bpm, 10); // Duration of a quarter note in seconds
        const selectedNoteDuration = quarterNoteDuration * (4 / noteValue) * 1000;

        return selectedNoteDuration;
    }

    render() {
        const { noEdit } = this.props;
        return (
            < Box >
                <Typography variant="h4" sx={{ marginTop: '10px', display: 'block' }}>Animated Diagram</Typography>
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: { xs: "min-content", md: "fit-content" }, }}>

                    {this.props.drawings.length !== 0 && (
                        <>
                            <img src="" alt="animated fretboard" ref={this.imageRef} />
                            <canvas style={{ visibility: 'hidden', display: 'none' }} ref={this.renderRef} />
                            <TextField
                                id="animted-svg-bpm"
                                label="BPM"
                                type="number"
                                defaultValue={this.state.bpm}
                                onChange={this.handleBpmChange}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                            {!noEdit && <Button onClick={this.handleExport} variant="outlined" startIcon={<CloudDownloadIcon />}>Animation</Button>}
                        </>
                    )}
                </div>
            </Box >
        );
    }
}

export default SVGAnimationComponent;